<template>
  <v-dialog v-model="activeModal" max-width="460" content-class="modal-personalizar">
    <v-card class="personalizar-card">
      <div class="personalizar-header">
        <div class="personalizar-heading">
          <span class="personalizar-title">{{ $t('links.personalizar') }}</span>
          <span class="personalizar-subtitle">
            {{ item.name }}
            <span class="personalizar-id">#{{ linkId }}</span>
          </span>
        </div>
        <v-icon class="personalizar-close" @click="closeModal">mdi-close</v-icon>
      </div>

      <div class="personalizar-body">
        <div class="personalizar-field" v-for="field in utmFields" :key="field.key">
          <label class="personalizar-label" :for="`personalizar-${field.key}`">{{ $t(field.label) }}</label>
          <v-text-field
            :id="`personalizar-${field.key}`"
            v-model="item.config[field.key]"
            dense
            outlined
            hide-details
            placeholder="-"
            class="personalizar-input"
          ></v-text-field>
        </div>

        <div class="personalizar-field">
          <label class="personalizar-label" for="personalizar-link">{{ $t('links.link') }}</label>
          <v-textarea
            id="personalizar-link"
            :value="linkPreview"
            readonly
            rows="2"
            auto-grow
            dense
            outlined
            hide-details
            :placeholder="$t('links.salveParaGerar')"
            class="personalizar-input personalizar-link"
          ></v-textarea>
        </div>
      </div>

      <div class="personalizar-footer">
        <v-btn text class="personalizar-copiar" :disabled="!linkPreview" @click="copiarLink">
          <v-icon small left>mdi-content-copy</v-icon>
          {{ $t('links.copiarLink') }}
        </v-btn>

        <v-btn
          depressed
          class="personalizar-salvar"
          :loading="loading"
          @click="salvar"
          v-if="podeEditar"
        >
          {{ $t('common.save') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { errorSnackbar, successSnackbar } from '@/core/service/utils'
import Events from '@/core/service/events'

export default {
  name: 'ModalPersonalizarLinks',
  data: () => ({
    activeModal: false,
    loading: false,
    // base do link vinda da campanha já persistida; a btag só existe depois do primeiro save
    shortUrl: '',
    btag: '',
    utmFields: [
      { key: 'utm_source', label: 'links.utmSource' },
      { key: 'utm_medium', label: 'links.utmMedium' },
      { key: 'utm_campaign', label: 'links.utmCampaign' },
      { key: 'utm_term', label: 'links.utmTerm' },
      { key: 'utm_content', label: 'links.utmContent' },
    ],
  }),
  computed: {
    ...mapGetters('roles', ['permiteAcao']),
    ...mapGetters('links', ['item']),
    ...mapGetters('auth', ['user']),
    isAffiliate() {
      if (!this.user.jsonFlags) return false
      return this.user.jsonFlags.affiliateId
    },
    podeEditar() {
      return this.permiteAcao(this.$route, 'edit') && this.isAffiliate
    },

    // #ID do link na Smartico: nomes podem repetir ("Other"), então o id identifica sem ambiguidade
    linkId() {
      return (this.item.config && this.item.config.link_id) || String(this.item.id || '').replace('smartico_', '')
    },

    // Preview montado na tela conforme o afiliado digita. Espelha as regras do backend
    // (SmarticoService.buildFinalUrl): btag primeiro, params vazios não entram na URL.
    // O backend continua sendo a fonte da verdade - ao salvar, shortUrl/btag vêm da resposta.
    linkPreview() {
      if (!this.shortUrl || !this.btag) return ''

      const separator = this.shortUrl.includes('?') ? '&' : '?'
      let url = `${this.shortUrl}${separator}btag=${this.btag}`

      this.utmFields.forEach(({ key }) => {
        const value = this.item.config && this.item.config[key]
        if (value) url += `&${key}=${encodeURIComponent(value)}`
      })

      return url
    },
  },
  mounted() {
    Events.$on('links::openModalPersonalizar', () => {
      this.shortUrl = this.item.url || ''
      this.btag = this.item.tag || ''
      this.activeModal = true
    })
  },
  methods: {
    ...mapActions('links', ['limparItem', 'editSmarticoLink']),
    closeModal() {
      this.activeModal = false
      this.shortUrl = ''
      this.btag = ''
      this.limparItem()
    },

    // Persiste os params customizados. No primeiro save é aqui que a btag é gerada, então é
    // também o que habilita o preview/cópia de um link que ainda não existia.
    salvar() {
      this.loading = true
      const linkId = parseInt(String(this.item.id).replace('smartico_', ''), 10)

      this.editSmarticoLink({
        linkId,
        utm_source: this.item.config.utm_source,
        utm_medium: this.item.config.utm_medium,
        utm_campaign: this.item.config.utm_campaign,
        utm_term: this.item.config.utm_term,
        utm_content: this.item.config.utm_content,
      })
        .then(response => {
          const data = response.data || {}
          if (!data.url) throw new Error(this.$t('links.erroGerarLink'))

          this.shortUrl = data.shortUrl || this.shortUrl
          this.btag = data.btag || this.btag
          successSnackbar(this.$t('links.linkSalvo'))
          Events.$emit('links::listaItens')
        })
        .catch(err => { errorSnackbar(err.error || err.message || this.$t('links.erroGerarLink')) })
        .finally(() => { this.loading = false })
    },

    copiarLink() {
      navigator.clipboard.writeText(this.linkPreview)
        .then(() => { successSnackbar(this.$t('links.linkCopiado')) })
        .catch(() => { errorSnackbar(this.$t('links.erroCopiar')) })
    },
  }
}
</script>

<style lang="scss" scoped>
.personalizar-card {
  background: #1D1D29 !important;
  border-radius: 12px;
  color: #FFF;
}

.personalizar-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid rgba(124, 124, 138, 0.2);
}

.personalizar-heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.personalizar-title {
  font-size: 17px;
  font-weight: 600;
  color: #FFF;
}

.personalizar-subtitle {
  font-size: 13px;
  font-weight: 500;
  color: #A1A1AA;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.personalizar-id {
  color: #6B7280;
  font-weight: 400;
  margin-left: 4px;
}

.personalizar-close {
  color: #7C7C8A !important;
  cursor: pointer;

  &:hover {
    color: #FFF !important;
  }
}

.personalizar-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 22px;
}

.personalizar-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.personalizar-label {
  font-size: 13px;
  font-weight: 600;
  color: #E4E4E7;
}

.personalizar-input::v-deep {
  .v-input__slot {
    background: #0D0F1D !important;
    border-radius: 8px;
    min-height: 44px;
  }

  fieldset {
    border-color: rgba(124, 124, 138, 0.25) !important;
  }

  input,
  textarea {
    color: #FFF !important;
    font-size: 14px;
  }

  input::placeholder,
  textarea::placeholder {
    color: #7C7C8A !important;
  }

  &.v-input--is-focused fieldset {
    border-color: var(--v-primary-base) !important;
  }
}

.personalizar-link::v-deep textarea {
  font-size: 13px;
  line-height: 1.5;
  word-break: break-all;
}

.personalizar-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 0 22px 22px;
}

// Cores vêm do tema Vuetify (montado a partir de src/core/brand), não de cores fixas
.personalizar-salvar {
  background: var(--v-primary-base) !important;
  color: #FFF !important;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  min-height: 42px;
  padding: 0 20px;
}

.personalizar-copiar {
  border: 1px solid rgba(124, 124, 138, 0.45);
  color: #FFF !important;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  min-height: 42px;
  padding: 0 16px;

  &::v-deep .v-icon {
    color: #FFF !important;
  }

  // No tema claro o Vuetify força rgba(0,0,0,.26) no texto de botão desabilitado, que fica preto
  // sobre o fundo escuro da modal. O override precisa incluir .theme--* para vencer em especificidade.
  &.theme--light.v-btn--disabled,
  &.theme--dark.v-btn--disabled {
    color: rgba(255, 255, 255, 0.55) !important;
    border-color: rgba(124, 124, 138, 0.25);

    ::v-deep .v-icon {
      color: rgba(255, 255, 255, 0.55) !important;
    }
  }
}
</style>
